import { useEffect, useState } from "react";
import { Button, Flex, Switch, Typography } from "antd";
import { FilterOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslate, useGetIdentity } from "@refinedev/core";
import { LateralModal } from "@/components/modals/LateralModal";
import {
  OperatorsFilter,
  fetchOperatorRows,
  mergeOperatorCards,
  toOperatorUserIds,
  type OperatorAffiliationSelection,
} from "@/components";
import { fetchDashboardOperators } from "../../api/dashboard.api";
import { marcarOperadoresVaciados, operadoresVaciadosAPropósito } from "../../utils";
import type { User } from "@/types/schema/schema.types";

const { Text } = Typography;

/**
 * El side panel de filtros del Panel de control — Fase 8 de `filtros-operadores-clientes`
 * (xcons-docs#309). Reemplaza al selector de operadores de xcons-docs#281.
 *
 * Sin toggle "Mi panel" (xcons-docs#318): con permiso para ver a otros, la propia tarjeta es un
 * operador más de "Operadores", pre-tildada por default la primera vez (o si nunca se vació a
 * propósito) — todos los usuarios del panel tienen al menos una afiliación, así que siempre
 * aparecen ahí. Sin ese permiso, el picker completo no aplica (dejaría elegir a cualquiera) y se
 * mantiene un toggle simple de "vos mismo sí/no". El footer "Borrar filtros" / "Filtrar" es el
 * mismo patrón de los demás side panels.
 *
 * Todo destildado es válido y significa "el store completo, sin identificar por operador" —
 * heredado de #281. La excepción de "Performance del operador" (D15) la resuelve el widget.
 */
export interface DashboardFiltersProps {
  /** userIds, incluida la propia tarjeta si está tildada — sin sentinel aparte. */
  value: string[];
  onChange: (ids: string[]) => void;
  storeCodes: string[];
  /** Sin store elegido no hay de dónde listar operadores, y las métricas tampoco se piden. */
  disabled?: boolean;
  /** Los badges tildados en esta sesión, para reabrir el panel como quedó. */
  seleccion: OperatorAffiliationSelection[];
  onSeleccion: (sel: OperatorAffiliationSelection[]) => void;
}

const DashboardFiltersForm = ({
  value,
  onChange,
  storeCodes,
  seleccion,
  onSeleccion,
  closeModal,
}: DashboardFiltersProps & { closeModal?: () => void }) => {
  const t = useTranslate();
  const { data: user } = useGetIdentity<User>();
  const myId = (user as any)?.id ? String((user as any).id) : null;
  const [draft, setDraft] = useState<OperatorAffiliationSelection[]>(seleccion);
  const [soloYo, setSoloYo] = useState(() => value.length > 0);
  const [puedeVerOtros, setPuedeVerOtros] = useState<boolean | null>(null);

  // Sin permiso sobre otros operadores el panel existe pero no ofrece a nadie más que uno mismo
  // (`features/Admin/home/home.md`). El flag sale del mismo endpoint de siempre.
  useEffect(() => {
    fetchDashboardOperators()
      .then((r) => setPuedeVerOtros(r.canViewOperatorDashboard))
      .catch(() => setPuedeVerOtros(false));
  }, []);

  // Sin nada guardado ni tildado en esta sesión: se pre-tilda la propia tarjeta, salvo que ya se
  // haya vaciado a propósito — mismo criterio que la preselección de store único heredada de #281.
  useEffect(() => {
    if (value.length || draft.length || !myId || !storeCodes.length) return;
    if (operadoresVaciadosAPropósito(myId)) return;
    const ctrl = new AbortController();
    fetchOperatorRows({ storeCodes, signal: ctrl.signal })
      .then((rows) => {
        const propia = mergeOperatorCards(rows).find(
          (c) => String(c.userId) === myId,
        );
        if (!ctrl.signal.aborted && propia) {
          setDraft(propia.badges.map((b) => ({ ...b, personName: propia.name })));
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, storeCodes.join(",")]);

  // Config con operadores guardados pero sin badges en esta sesión (recarga): se tildan todas las
  // afiliaciones de esas personas, que es lo que el filtro por persona significa.
  useEffect(() => {
    const guardados = value.map(Number).filter(Number.isFinite);
    if (!guardados.length || draft.length) return;
    const ctrl = new AbortController();
    fetchOperatorRows({ storeCodes, signal: ctrl.signal })
      .then((rows) => {
        const quiero = new Set(guardados);
        const sel: OperatorAffiliationSelection[] = [];
        for (const card of mergeOperatorCards(rows)) {
          if (!quiero.has(card.userId)) continue;
          for (const b of card.badges)
            sel.push({ ...b, personName: card.name });
        }
        if (!ctrl.signal.aborted && sel.length) setDraft(sel);
      })
      .catch(() => {});
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicar = () => {
    const ids =
      puedeVerOtros === false
        ? soloYo && myId
          ? [myId]
          : []
        : toOperatorUserIds(draft).map(String);
    if (puedeVerOtros !== false) onSeleccion(draft);
    if (myId) marcarOperadoresVaciados(myId, ids.length === 0);
    onChange(ids);
    closeModal?.();
  };

  const limpiar = () => {
    setSoloYo(false);
    setDraft([]);
    onSeleccion([]);
    if (myId) marcarOperadoresVaciados(myId, true);
    onChange([]);
    closeModal?.();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        {puedeVerOtros === false ? (
          <Flex
            align="center"
            justify="space-between"
            style={{ padding: "4px 0" }}
          >
            <Flex align="center" gap={8}>
              <UserOutlined
                style={{ color: soloYo ? "#15744b" : "rgba(0,0,0,0.35)" }}
              />
              <Text style={{ fontWeight: soloYo ? 600 : 400 }}>
                {t("dashboard.filters.myPanel")}
              </Text>
            </Flex>
            <Switch size="small" checked={soloYo} onChange={setSoloYo} />
          </Flex>
        ) : (
          <>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              {t("filter.operators.title")}
            </Text>
            <OperatorsFilter
              value={draft}
              onChange={setDraft}
              storeCodes={storeCodes}
              disabled={puedeVerOtros === null}
              chipsInline={false}
            />
          </>
        )}
      </div>
      <Flex
        justify="end"
        gap={8}
        style={{
          flexShrink: 0,
          padding: "12px 24px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fff",
        }}
      >
        <Button type="default" danger onClick={limpiar}>
          {t("filter.button.clearFilters")}
        </Button>
        <Button type="primary" icon={<FilterOutlined />} onClick={aplicar}>
          {t("filter.button.filter")}
        </Button>
      </Flex>
    </div>
  );
};

export const DashboardFilters = (props: DashboardFiltersProps) => {
  const t = useTranslate();
  const activos = props.value.length;
  return (
    // Mismo tamaño que el botón "Filtros" de Contactos/Presupuestos/Pedidos: sin `buttonSize`,
    // `LateralModal` usa su default `"middle"` (xcons-docs#312). El contador va dentro del botón,
    // como en el resto de la app.
    <LateralModal
      buttonName={
        activos ? `${t("buttons.filters")} (${activos})` : t("buttons.filters")
      }
      title={t("buttons.filters")}
      disabled={props.disabled}
    >
      <DashboardFiltersForm {...props} />
    </LateralModal>
  );
};
